import { ConfirmOptions } from '@utils/types/confirm-options';
import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useConfirm } from '@utils/providers/confirm-context.tsx';

export const usePrompt = ({
  isDirty = false,
  title,
  subtitle,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'warning'
}: ConfirmOptions & { isDirty?: boolean }) => {
  const blocker = useBlocker(isDirty);
  const { show } = useConfirm();

  // Always hold the latest blocker so async callbacks never close over a stale one
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  // Prevent a second prompt from opening while one is already in flight
  const promptActiveRef = useRef(false);

  const confirm = useCallback(
    (secondOnComplete?: () => void, secondOnCancel?: () => void) => {
      if (!isDirty) return Promise.resolve(true);
      return new Promise<boolean>(resolve => {
        show({
          title,
          subtitle,
          confirmText,
          cancelText,
          type,
          onConfirm: () => {
            resolve(true);
            onConfirm?.();
            secondOnComplete?.();
          },
          onCancel: () => {
            resolve(false);
            onCancel?.();
            secondOnCancel?.();
          }
        });
      });
    },
    [
      cancelText,
      confirmText,
      isDirty,
      onCancel,
      onConfirm,
      show,
      subtitle,
      title,
      type
    ]
  );

  // React Router creates a new blocker object on every render, so depending on
  // the full `blocker` object would re-fire this effect even when state is
  // unchanged.  Depend only on `blocker.state` to avoid that.
  const blockerState = blocker.state;

  useEffect(() => {
    if (blockerState !== 'blocked') return;
    if (promptActiveRef.current) return;

    promptActiveRef.current = true;
    let cancelled = false;

    confirm().then(result => {
      promptActiveRef.current = false;
      if (cancelled) return;

      // Use the ref so we always act on the current blocker, not the one
      // captured at effect-run time.
      const current = blockerRef.current;
      if (current.state !== 'blocked') return;

      if (result) current.proceed();
      else current.reset();
    });

    // If the effect re-runs before the promise settles (e.g. isDirty flipped),
    // mark the in-flight promise as stale so it won't call proceed/reset.
    return () => {
      cancelled = true;
      promptActiveRef.current = false;
    };
    // confirm is intentionally excluded: its identity changes with every
    // option prop change, but the modal it opens captures the right callbacks
    // at call time. Adding it would cause duplicate prompts on re-renders.
  }, [blockerState]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        const message =
          subtitle ||
          'You have unsaved changes. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, subtitle]);

  return {
    confirm
  };
};
